'use client';

import { createClient } from '../supabase/client';

export interface Subscription {
  id: string;
  userId: string;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string;
  stripePriceId: string | null;
  tier: 'silver' | 'gold' | 'platinum' | 'diamond';
  status: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired' | 'unpaid';
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;
  trialStart: string | null;
  trialEnd: string | null;
  createdAt: string;
  updatedAt: string;
}

function mapSubscription(row: any): Subscription {
  return {
    id: row.id,
    userId: row.user_id,
    stripeSubscriptionId: row.stripe_subscription_id,
    stripeCustomerId: row.stripe_customer_id,
    stripePriceId: row.stripe_price_id,
    tier: row.tier,
    status: row.status,
    currentPeriodStart: row.current_period_start,
    currentPeriodEnd: row.current_period_end,
    cancelAtPeriodEnd: row.cancel_at_period_end,
    canceledAt: row.canceled_at,
    trialStart: row.trial_start,
    trialEnd: row.trial_end,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const subscriptionService = {
  async getSubscription(): Promise<Subscription | null> {
    const supabase = createClient();
    const { data, error } = await supabase.functions.invoke('get-subscription');
    if (error) {
      console.error('getSubscription error:', error);
      return null;
    }
    return data?.subscription ? mapSubscription(data.subscription) : null;
  },

  async createCheckoutSession(priceId: string, tier: string): Promise<string | null> {
    const supabase = createClient();
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        priceId,
        tier,
        successUrl: `${window.location.origin}/dashboard?subscription=success`,
        cancelUrl: `${window.location.origin}/homepage#pricing`,
      },
    });
    if (error) {
      console.error('createCheckoutSession error:', error);
      throw new Error((data as any)?.error ?? error.message ?? 'Checkout failed');
    }
    return data?.url ?? null;
  },

  async createPortalSession(): Promise<string | null> {
    const supabase = createClient();
    const { data, error } = await supabase.functions.invoke('create-portal-session', {
      body: { returnUrl: `${window.location.origin}/settings` },
    });
    if (error) {
      console.error('createPortalSession error:', error);
      throw new Error((data as any)?.error ?? error.message ?? 'Portal session failed');
    }
    return data?.url ?? null;
  },

  isActive(subscription: Subscription | null): boolean {
    return subscription?.status === 'active' || subscription?.status === 'trialing';
  },

  getTierLabel(tier: string): string {
    const labels: Record<string, string> = {
      silver: 'Silver',
      gold: 'Gold',
      platinum: 'Platinum',
      diamond: 'Diamond',
    };
    return labels[tier] ?? tier;
  },

  getTierColor(tier: string): string {
    const colors: Record<string, string> = {
      silver: '#94a3b8',
      gold: '#fbbf24',
      platinum: '#e2e8f0',
      diamond: '#A3D4FF',
    };
    return colors[tier] ?? '#A3D4FF';
  },
};
